import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { categoryNameMap, type Video } from "@/mocks/videos";

interface HeroCarouselProps {
  items: Video[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [current, setCurrent] = useState(0);

  const go = useCallback(
    (dir: number) => {
      setCurrent((prev) => (prev + dir + items.length) % items.length);
    },
    [items.length]
  );

  useEffect(() => {
    const timer = setInterval(() => go(1), 6000);
    return () => clearInterval(timer);
  }, [go]);

  if (items.length === 0) return null;

  return (
    <section className="relative w-full h-[480px] md:h-[640px] overflow-hidden bg-foreground-950">
      {items.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === current ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={item.heroCover || item.cover}
            alt={`${item.title} - 亲吻视频推荐`}
            title={`${item.title} - 亲嘴视频`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/45 to-foreground-950/40" />
        </div>
      ))}

      <div className="relative z-10 w-full h-full flex items-center">
        <div className="w-full px-4 md:px-8 max-w-3xl pb-10">
          <div className="flex items-center gap-2 mb-4">
            <span className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-label">
              焦点推荐
            </span>
            <span className="px-3 py-1 rounded-full bg-background-50/15 text-background-100 text-xs font-label backdrop-blur">
              {categoryNameMap[items[current].categoryKey]}
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-background-50 leading-tight mb-4">
            {items[current].title}
          </h1>
          <p className="text-sm md:text-base text-background-200/90 leading-relaxed line-clamp-3 max-w-2xl mb-8">
            {items[current].description}
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <Link
              to={`/detail/${items[current].id}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-fill text-lg" />
              立即观看
            </Link>
            <Link
              to={`/category/${items[current].categoryKey}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50/15 text-background-50 text-sm font-medium hover:bg-background-50/25 backdrop-blur transition-colors cursor-pointer whitespace-nowrap"
            >
              浏览同类
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 md:bottom-8 right-4 md:right-8 z-20 flex items-center gap-3">
        <button
          aria-label="上一个"
          onClick={() => go(-1)}
          className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/20 text-background-50 hover:bg-primary-500 backdrop-blur transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <button
          aria-label="下一个"
          onClick={() => go(1)}
          className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/20 text-background-50 hover:bg-primary-500 backdrop-blur transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      </div>

      <div className="absolute bottom-8 left-4 md:left-8 z-20 hidden md:flex items-center gap-2">
        {items.map((item, index) => (
          <button
            key={item.id}
            aria-label={`切换到${item.title}`}
            onClick={() => setCurrent(index)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              index === current
                ? "w-8 bg-primary-500"
                : "w-4 bg-background-50/40 hover:bg-background-50/70"
            }`}
          />
        ))}
      </div>
    </section>
  );
}